import React, { useCallback, useMemo, useState } from 'react'
import './account-checking.scss'
import { useRequest, useUpdateEffect, useDebounceFn } from 'ahooks'
import { $post } from '@/api'
import AccountChart from './account-chart/account-chart'
import { IAccountChecking } from './account-chart/type'
import dayjs from 'dayjs'
import { formatDay, formatPrice } from '@/utils'
import useBemClass from '@/utils/hooks/useBEMClass'
import { Expaned, Row, Column } from '@/components/layout/layout'
import classnames from 'classnames'
import { DatePicker, Spin, Tooltip } from 'antd'
import AccountTable from './account-table/account-table'
import { AutoResizer } from '@/utils/utils-widget'
import { showLayer } from '@/utils/layer'
import SettleRecord from './settle-record/settle-record'
import AccountFilter from './account-filter/account-filter'
import { accountFilterStore } from './account-filter/filter-store'
import { observer } from 'mobx-react'
import { useDataRefresh } from '@/utils/hooks/useRefresh'
import OrderDataRefresh from '../../order-manage/order-cashier/order-refresh/order-refresh'
import Tip from '@/components/tip/tip'
import Big from 'big.js'
import moment from 'moment'
import DataDateSelect, { scopeTypeMap } from '../components/data-date-select/data-date-select'

export interface IAccountData {
    date: string
    payment_data: {
        name: string
        id: number
        amount: number
    }[]
}

const accountPaymentTipMap: Record<string, string> = {
    '0': '总收款=本店所有收款方式所收款项总和',
    '2': '通过扫码枪扫描顾客微信付款码收款',
    '3': '通过扫码枪扫描顾客支付宝付款码收款',
    '4': '顾客通过扫描个人微信收款码所收款项',
    '5': '顾客通过扫描个人支付宝收款码所收款项',
}

export const accountPaymentMap: Record<string, string> = {
    '0': '总收款',
    '1': '现金',
    '2': '微信',
    '3': '支付宝',
    '4': '个人微信',
    '5': '个人支付宝',
    '6': '银行卡',
    '7': '云闪付',
    '-1': '其它',
    '-2': '收银员',
}

export const getDateByScope = (scope: number) => {
    let from: string | undefined
    let to: string | undefined
    switch (scope) {
        case 0:
            from = dayjs().subtract(14, 'day').format('YYYY-MM-DD')
            to = dayjs().format('YYYY-MM-DD')
            break
        case 1:
            from = dayjs().startOf('week').subtract(7, 'week').add(1, 'd').format('YYYY-MM-DD')
            to = dayjs().endOf('week').add(1, 'd').format('YYYY-MM-DD')
            break
        case 2:
            from = dayjs().startOf('month').subtract(5, 'month').format('YYYY-MM-DD')
            to = dayjs().endOf('month').format('YYYY-MM-DD')
            break
        case 3:
            from = dayjs().startOf('year').subtract(2, 'year').format('YYYY-MM-DD')
            to = dayjs().endOf('year').format('YYYY-MM-DD')
            break
        default:
            break
    }

    return [from, to]
}

export const useAccountCompute = () => {
    const computeTotalPrice = useCallback((record: IAccountChecking.PaymentOverview) => {
        const paymentTotal = record.payment_data.reduce((prev, next) => {
            return prev + next.amount
        }, 0)

        const customPaymentTotal = record.custom_payment_data.reduce((prev, next) => {
            return prev + next.amount
        }, 0)

        const total = +(paymentTotal + customPaymentTotal).toFixed(2)

        return total
    }, [])

    const getPaymentData = useCallback(
        (record: IAccountChecking.PaymentOverview) => {
            const customPaymentData = record.custom_payment_data.map(c => ({
                name: c.custom_payment.name,
                id: c.custom_payment.id,
                amount: c.amount,
            }))
            const totalData = {
                name: '总收款',
                id: 0,
                amount: computeTotalPrice(record),
            }
            const cashierData = {
                name: '收银员',
                id: -2,
                amount: record.cashiers,
            }
            const paymentData = record.payment_data
                .map(p => ({
                    name: accountPaymentMap[p.payment_type],
                    id: p.payment_type,
                    amount: p.amount,
                    sort: p.payment_type === -1 ? -1 : 0,
                }))
                .sort((a, b) => b.sort - a.sort)

            return {
                paymentData,
                totalData,
                customPaymentData,
                cashierData,
            }
        },
        [computeTotalPrice],
    )

    const getScenePaymentData = useCallback(
        (overview: IAccountChecking.IOverView) => {
            const cashierData = getPaymentData(overview.cashier_statistics_info)
            const mallData = getPaymentData(overview.mall_statistics_info)

            const cashierPaymentData = cashierData.paymentData.reduce((pre, next) => {
                pre[`${next.id}`] = next

                return pre
            }, {} as Record<string, Record<string, string | number>>)

            const cashierCustomPaymentData = cashierData.customPaymentData.reduce((pre, next) => {
                pre[`${next.id}`] = next

                return pre
            }, {} as Record<string, Record<string, string | number>>)

            return {
                mallTotalData: mallData.totalData,
                mallCustomPaymentData: mallData.customPaymentData,
                cashierCashiers: cashierData.cashierData,
                mallPaymentData: mallData.paymentData,
                cashierTotalData: cashierData.totalData,
                cashierCustomPaymentData: cashierData.customPaymentData,
                cashierPaymentData: cashierData.paymentData,
                totalData: {
                    ...cashierData.totalData,
                    amount: +Big(cashierData.totalData.amount).plus(mallData.totalData.amount),
                },
                paymentData: mallData.paymentData.map(p => ({
                    ...p,
                    amount: +Big(p.amount).plus(cashierPaymentData[p.id].amount),
                })),
                customPaymentData: mallData.customPaymentData.map(p => ({
                    ...p,
                    amount: +Big(p.amount).plus(cashierCustomPaymentData[p.id].amount),
                })),
            }
        },
        [getPaymentData],
    )

    const getCombineData = useCallback(
        (overview: IAccountChecking.IOverView) => {
            const {
                customPaymentData: tmpCustomPaymentData,
                totalData: tmpTotalData,
                paymentData: tmpPaymentData,
                cashierCashiers,
                mallCustomPaymentData,
                mallPaymentData,
                mallTotalData,
                cashierCustomPaymentData,
                cashierPaymentData,
                cashierTotalData,
            } = getScenePaymentData(overview)

            const totalData = {
                ...tmpTotalData,
                amount: tmpTotalData.amount,
                mallAmount: mallTotalData.amount,
                cashierAmount: cashierTotalData.amount,
            }

            const cashiersData = {
                ...cashierCashiers,
                amount: cashierCashiers.amount,
                mallAmount: 0,
                cashierAmount: cashierCashiers.amount,
            }

            const paymentData = tmpPaymentData.map((p, i) => ({
                ...p,
                amount: p.amount,
                mallAmount: mallPaymentData[i].amount,
                cashierAmount: cashierPaymentData[i].amount,
            }))

            const customPaymentData = tmpCustomPaymentData.map((p, i) => ({
                ...p,
                amount: p.amount,
                mallAmount: mallCustomPaymentData[i].amount,
                cashierAmount: cashierCustomPaymentData[i].amount,
            }))

            return {
                customPaymentData,
                paymentData,
                cashiersData,
                totalData,
            }
        },
        [getScenePaymentData],
    )

    return {
        getPaymentData,
        computeTotalPrice,
        getScenePaymentData,
        getCombineData,
    }
}

export const useShowShadow = () => {
    const [showShadow, setShowShadow] = useState<boolean>(false)

    const { run: scrollRun } = useDebounceFn(
        (e: number) => {
            setShowShadow(e > 10)
        },
        {
            wait: 10,
        },
    )

    return {
        showShadow,
        onScroll: (e: any) => {
            scrollRun(e.target.scrollTop)
        },
    }
}

const AccountChecking = observer(() => {
    const cls = useBemClass('account-checking')
    const [scope, setScope] = useState<number>(0)
    const [date, setDate] = useState<(string | undefined)[]>([
        dayjs().subtract(14, 'day').format('YYYY-MM-DD'),
        dayjs().format('YYYY-MM-DD'),
    ])
    const [tip, setTip] = useState<string>('当前展示近15日走势')
    const [showTip, setShowTip] = useState<boolean>(true)
    const [showFilter, setShowFilter] = useState<boolean>(false)
    const [customPayment, setCustomPayment] = useState<
        Array<{
            id: number
            name: string
        }>
    >([])
    const { getCombineData } = useAccountCompute()

    const { onScroll, showShadow } = useShowShadow()

    const accountReq = useRequest<{
        data_list: IAccountChecking.IOverView[]
        sums: IAccountChecking.IOverView
        cashiers: IAccountChecking.Cashier[]
    }>(
        () =>
            $post({
                url: '/finance/getfundstatistics',
                params: {
                    from_date: date[0],
                    scope: scope,
                    to_date: date[1],
                    ...accountFilterStore.filterQuery,
                },
            }),
        {
            refreshDeps: [date, scope, accountFilterStore.filterQuery],
            onSuccess: res => {
                setCustomPayment(
                    res.sums.cashier_statistics_info.custom_payment_data.map(c => ({
                        id: c.custom_payment.id,
                        name: c.custom_payment.name,
                    })),
                )
            },
        },
    )

    useUpdateEffect(() => {
        setDate(getDateByScope(scope))
    }, [scope])

    const getDayByType = useCallback((row: IAccountChecking.IOverView) => {
        let fromDate = ''
        let toDate = ''
        if (row.scope === 0) {
            // 日
            fromDate = row.date
            toDate = row.date
        } else if (row.scope === 1) {
            // 周
            const month = dayjs(`${row.year_on_week}-${row.month_on_week}-01`)
            let firstDay = month
            if (month.day() !== 1) {
                // 如果某月1号不是星期一，则向1号后面找到第一个星期一作为第一周第一天
                const weekday = month.day() === 0 ? 7 : month.day() // 星期
                firstDay = month.add(8 - weekday, 'day') // 找到星期一那一天，那么就是第一周第一天
            }
            fromDate = firstDay.add(7 * ((row.week_of_month || 0) - 1), 'day').format('YYYY-MM-DD')
            toDate = dayjs(fromDate).add(6, 'day').format('YYYY-MM-DD')
        } else if (row.scope === 2) {
            // 月
            fromDate = row.date
            toDate = dayjs(row.date).add(1, 'month').subtract(1, 'day').format('YYYY-MM-DD')
        } else if (row.scope === 3) {
            // 年
            fromDate = row.date
            toDate = dayjs(row.date).add(1, 'year').subtract(1, 'day').format('YYYY-MM-DD')
        }

        return {
            from_date: fromDate,
            to_date: toDate,
        }
    }, [])

    const getDateByType = useCallback((data: IAccountChecking.IOverView) => {
        if (data.scope === 0) return formatDay(data.date)
        if (data.scope === 1) {
            return `${data.month_on_week}月第${data.week_of_month}周`
        }
        if (data.scope === 2) {
            const day = dayjs(data.date)

            return `${day.format('YY')}年${day.format('M')}月`
        }

        return `${+data.date.substring(0, 4)}`
    }, [])

    const sumData = useMemo(() => {
        if (!accountReq.data) {
            return []
        } else {
            const { totalData, paymentData, customPaymentData } = getCombineData(accountReq.data.sums)

            return [totalData, ...paymentData, ...customPaymentData]
        }
    }, [accountReq.data, getCombineData])

    const accountData = useMemo(() => {
        if (!accountReq.data) {
            return []
        } else {
            return accountReq.data.data_list.map(d => {
                const { customPaymentData, totalData, paymentData, cashiersData } = getCombineData(d)

                const payment_data = [totalData, ...paymentData, cashiersData, ...customPaymentData]
                const payment_table_data = payment_data.reduce((pre, next) => {
                    pre[`payment:${next.id}`] = next

                    return pre
                }, {} as Record<string, any>)

                const day = getDayByType(d)

                return {
                    ...day,
                    date: getDateByType(d),
                    payment_data: [...customPaymentData, ...paymentData.reverse(), totalData],
                    cashiers: d.cashier_statistics_info.cashiers,
                    ...payment_table_data,
                }
            })
        }
    }, [accountReq.data, getCombineData, getDateByType, getDayByType])

    const { refresh, lastRefreshTime, loading } = useDataRefresh({
        url: '/finance/runfundstatistics',
        storageKey: 'account-data-refresh-time',
        onSuccess: () => {
            accountReq.refresh()
        },
    })

    return (
        <Expaned className={cls()}>
            <div className={cls('header')}>
                <OrderDataRefresh loading={loading} refreshTime={lastRefreshTime} onClick={refresh} />
            </div>
            {showFilter && (
                <AccountFilter onClose={() => setShowFilter(false)} staffs={accountReq.data?.cashiers || []} />
            )}
            <div className={cls('tip')}>
                <div>*资金对账统计实际收到的资金，包括下单、充值等。每个支付方式为已扣除退款的净收入。</div>
                <Row data-align-items-center>
                    <div data-color-999 data-font-size-12 data-margin-left-16>
                        在线支付后在清算后划款至银行卡
                    </div>
                    <div
                        className="action"
                        style={{ fontSize: '12px' }}
                        data-margin-left-16
                        onClick={() => {
                            showLayer(<SettleRecord />)
                        }}
                    >
                        清算记录
                    </div>
                </Row>
            </div>
            {!!accountFilterStore.filterText && (
                <Row className={cls('searchBox')}>
                    <div>
                        筛选：
                        <span className="them-color" data-font-weight-600 data-padding-h-8>
                            {accountFilterStore.filterText}
                        </span>
                    </div>
                    <div
                        className={cls('delete')}
                        onClick={() => {
                            accountFilterStore.reset()
                        }}
                    >
                        <i data-cursor-pointer className="iconfont icon6guanbi-20" />
                    </div>
                </Row>
            )}
            <Spin spinning={accountReq.loading}>
                <Expaned data-column data-overflow-hidden className={cls('content')}>
                    <Row data-align-items-center data-margin-top-16 data-justify-content-space-between>
                        <Row data-align-items-center>
                            <DataDateSelect
                                onClick={(from, to, scope, t) => {
                                    setDate([from, to])
                                    setScope(scope)
                                    setTip(t)
                                    setShowTip(true)
                                }}
                            />
                        </Row>
                        <Row data-align-items-center>
                            <div hidden={!showTip} data-color-999 data-font-size-14 data-margin-right-10>
                                {tip}
                            </div>
                            <div className="time-picker">
                                <DatePicker.RangePicker
                                    picker={scopeTypeMap[scope] as any}
                                    dateRender={current => (
                                        <div
                                            className="ant-picker-cell-inner"
                                            style={{
                                                borderRadius: '50%',
                                            }}
                                        >
                                            {current.date()}
                                        </div>
                                    )}
                                    value={[moment(date[0]), moment(date[1])]}
                                    onChange={monent => {
                                        if (monent) {
                                            setShowTip(false)
                                            if (scopeTypeMap[scope] === 'month') {
                                                if (!monent) return
                                                const start_date = monent[0]?.format('YYYY-MM-DD')
                                                const end_date = monent[1]
                                                    ?.set('date', 1)
                                                    .add(1, 'month')
                                                    .subtract(1, 'days')
                                                    .format('YYYY-MM-DD') // 取这个月最后一天
                                                setDate([start_date, end_date])
                                            } else if (scopeTypeMap[scope] === 'year') {
                                                const start_date = monent[0]?.format('YYYY-MM-DD')
                                                const end_date = monent[1]
                                                    ?.add(1, 'year')
                                                    .set('date', 1)
                                                    .set('month', 0)
                                                    .subtract(1, 'days')
                                                    .format('YYYY-MM-DD') // 取这一年最后一天
                                                setDate([start_date, end_date])
                                            } else {
                                                monent[0] && setDate(monent?.map(m => m?.format('YYYY-MM-DD')))
                                            }
                                        }
                                    }}
                                    suffixIcon={<i className="iconfont iconshijianshaixuan" />}
                                />
                            </div>
                            <div
                                className={cls('filter')}
                                data-cursor-pointer
                                onClick={() => setShowFilter(val => !val)}
                                style={{
                                    color: !accountFilterStore.filterText ? '#333' : '#14cc9b',
                                }}
                            >
                                <i className="iconfont icon6shaixuan-20" data-font-size-20 />
                                <span data-margin-left-8>筛选</span>
                            </div>
                        </Row>
                    </Row>
                    <Row
                        className={classnames(cls('sum'), {
                            [cls('sum', 'shadow')]: showShadow,
                        })}
                    >
                        {sumData.map(s => (
                            <div key={s.id} className={cls('total')}>
                                <div className="popover-blank">
                                    <Tooltip
                                        placement="top"
                                        arrowPointAtCenter
                                        overlayClassName="custom-popover"
                                        title={
                                            <Column data-color-333 data-font-size-14>
                                                <div>收银台：{formatPrice(s.cashierAmount)}</div>
                                                <div>微商城：{formatPrice(s.mallAmount)}</div>
                                            </Column>
                                        }
                                    >
                                        <div data-cursor-pointer className={cls('total', 'num')}>
                                            ¥{s.amount}
                                        </div>
                                    </Tooltip>
                                </div>
                                <Row data-margin-top-4>
                                    <div className={cls('total', 'des')}>{s.name}</div>
                                    {!!accountPaymentTipMap[s.id] && (
                                        <div data-margin-left-8>
                                            <Tip text={accountPaymentTipMap[s.id]} />
                                        </div>
                                    )}
                                </Row>
                            </div>
                        ))}
                    </Row>
                    <Expaned data-overflow-auto className={cls('no-scroll')} onScroll={onScroll}>
                        <div className={cls('chart')}>
                            <AccountChart dataList={accountData || []} />
                        </div>

                        <AutoResizer>
                            {({ height }) => (
                                <div className={cls('table')}>
                                    <AccountTable
                                        customPaymentData={customPayment}
                                        height={height}
                                        // @ts-ignore
                                        dataList={accountData || []}
                                        loading={accountReq.loading}
                                    />
                                </div>
                            )}
                        </AutoResizer>
                    </Expaned>
                </Expaned>
            </Spin>
        </Expaned>
    )
})

export default AccountChecking
